import { computed } from "vue";
import { DS_USER_CLASSES, DS_USER_STATUS_TO_CLASS_MAP } from "@/src/design_system/data_display/user/constants/classes";
import { DSUserEmits } from "@/src/design_system/data_display/user/types/emits";
import { DSUserResolvedProps } from "@/src/design_system/data_display/user/types/props";
import { DS_USER_SIZE } from "@/src/design_system/data_display/user/types/size";
import { DSUserAvatarProps } from "@/src/design_system/data_display/user/types/avatarProps";
import { DS_USER_STATUS } from "@/src/design_system/data_display/user/types/status";

export const useDSUser = (props: Readonly<DSUserResolvedProps>, emit: DSUserEmits) => {
  const size = computed(() => {
    return Object.values(DS_USER_SIZE).includes(props.size) ? props.size : DS_USER_SIZE.Large;
  });
  const status = computed(() => {
    return Object.values(DS_USER_STATUS).includes(props.avatarStatus) ? props.avatarStatus : DS_USER_STATUS.None;
  });

  const hasHref = computed(() => props.href !== undefined);
  const isInteractive = computed(() => hasHref.value || props.hasAction);
  const rootAriaLabel = computed(() => {
    if (props.ariaLabel) return props.ariaLabel;
    if (props.name || props.statusText) return undefined;

    return props.avatarAlt || props.avatarText || undefined;
  });
  const rootElement = computed(() => {
    if (hasHref.value) return "a";
    return props.hasAction ? "button" : "div";
  });
  const rootAttributes = computed(() => {
    if (hasHref.value) return { href: props.href, "aria-label": rootAriaLabel.value };
    if (!props.hasAction) return {};

    return { type: "button", "aria-label": rootAriaLabel.value };
  });

  const userClasses = computed(() => {
    return [
      DS_USER_CLASSES.Root,
      `${DS_USER_CLASSES.Root}--${size.value}`,
      { [DS_USER_CLASSES.Action]: isInteractive.value },
    ];
  });

  const statusClasses = computed(() => {
    return [DS_USER_CLASSES.Status, DS_USER_STATUS_TO_CLASS_MAP[status.value]];
  });

  const avatarProps = computed<DSUserAvatarProps>(() => {
    return {
      size: size.value,
      status: status.value,
      src: props.avatarSrc,
      alt: props.avatarAlt ?? (!isInteractive.value && !props.name && !props.statusText ? props.ariaLabel : ""),
      text: props.avatarText,
      iconName: props.avatarIconName,
      ariaLabel: isInteractive.value ? undefined : props.ariaLabel,
    };
  });

  const handleClick = (event: MouseEvent) => {
    if (!isInteractive.value) return;

    emit("click", event);
  };

  return {
    avatarProps,
    handleClick,
    rootAttributes,
    rootElement,
    statusClasses,
    userClasses,
  };
};
